/* global PayWithMyBank */
/* eslint-disable */
import { mapActions, mapState } from 'vuex';
import { omitBy, isNil, isNumber, get } from 'lodash';
import { loadScript } from '@/utils/helpers';
import loaderMixin from '@/mixins/loader';
import promoCodeMixin from '@/mixins/promoCode';
import { MODALS } from '@/components/app/app-modals/modals-constants';
import documentUploadMixin from '@/mixins/documentUpload';
import { validators } from '@/components/forms/mixin';
import getValidationMessage from '@/helpers/getValidationMessage';
import deposited from '@/components/wallet/deposited/deposited.vue';
import { validationMixin } from 'vuelidate';
import bankAccount from './components/bank-account/bank-account.vue';
import processingTransaction from './components/processing-transaction/processing-transaction.vue';
import depositWarningMixin from '@/mixins/depositWarningMixin';
import { withMeta } from '@/helpers/dataLayer';
import depositDisclaimer from '@/components/wallet/shared/deposit-disclaimer/deposit-disclaimer.vue';
import { DEPOSIT_METHOD_TYPE } from '@/components/wallet/shared/depositMethodTypes';
import feeWaivedBanner from '@/components/wallet/shared/fee-waived-banner/fee-waived-banner.vue';
import { STEPS, FINISHED_STATUS } from './add-funds-bank-transfer-constants';

const AMOUNT_STEP_ONE = 10;
const AMOUNT_STEP_THREE = 50;
const AMOUNT_STEP_FOUR = 100;

const CLOSE_PAYMENT_SUCCESS_TIMEOUT = 1500;

const ERROR_MESSAGE_GENERIC = 'Unable to remove the authorization, please try again later.';

const randomNumber = (min = 1, max = 99999) => Math.floor(Math.random() * (max - min + 1) + min);

export default {
  mixins: [
    documentUploadMixin,
    validationMixin,
    loaderMixin,
    promoCodeMixin,
    depositWarningMixin,
  ],
  components: {
    bankAccount,
    processingTransaction,
    deposited,
    depositDisclaimer,
    feeWaivedBanner,
  },
  props: {
    isPopup: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      depositMethodType: DEPOSIT_METHOD_TYPE.ONLINE_BANKING,
      errors: [],
      fields: {
      },
      isFormEnabled: true,
      timeout: null,
      amount: 0,
      payWithMyBankId: `paywithmybank-${randomNumber()}`,
      canDeposit: false,
      smallPopupShown: false,
      depositAmounts: [
        AMOUNT_STEP_ONE,
        AMOUNT_STEP_THREE,
        AMOUNT_STEP_FOUR,
      ],
      currentStep: STEPS.INITIAL,
      currentStepData: {},
      requestSignature: '',
      authorizationId: null,
      errorMessage: '',
      refreshDataObject: {},
      existingAccounts: [],
      account: null,
      STEPS,
    };
  },
  validations() {
    return {
      amount: validators.depositAmountCoinbase(
        this.minimumDepositAmount, this.maximumDepositAmount),
    };
  },
  computed: {
    ...mapState('wallet', ['minimumDepositAmount', 'bankTransferStep']),
    ...mapState('user', ['status']),
    accountUploadDocumentTheme() {
      return this.isPopup ? '' : 'page-padded';
    },
    classModifiers() {
      return {
        'add-funds-bank-transfer': true,
        'add-funds-bank-transfer--popup': this.isPopup,
        'add-funds-bank-transfer--regular': !this.isPopup,
      };
    },
    noticePromoModifiers() {
      return {
        'add-funds-bank-transfer__notice': true,
        'add-funds-bank-transfer__notice--promo-popup': this.isPopup,
      };
    },
    infoAmount() {
      const number = Number(this.amount).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
      return `$${number}`;
    },
    isConfirmDisabled() {
      return this.invalidOrDisabled || !this.canDeposit;
    },
    amountHasError() {
      return this.$v.amount.$error;
    },
    amountValidationMessage() {
      return getValidationMessage(this.$v.amount);
    },
    maximumDepositAmount() {
      return 99999;
    },
    isFormDisabled() {
      return this.isFormEnabled === false;
    },
    invalidOrDisabled() {
      return this.$v.$invalid || !this.isFormEnabled;
    },
  },
  methods: {
    ...mapActions('popup', [
      'setCurrentSmallPopup', 'setCurrentPopupOptions', 'setCurrentPopup',
    ]),
    ...mapActions('wallet', [
      'getDepositPWMB', 'deletePWMBAccount',
      'confirmDepositPWMB', 'submitDepositPWMB',
      'getDepositMethods',
    ]),
    formIsValid() {
      return !this.$v.$invalid;
    },
    formEnable() {
      this.isFormEnabled = true;
    },
    formDisable() {
      this.formReset();
      this.isFormEnabled = false;
      return true;
    },
    formReset() {
      this.errors = [];
    },
    onSuccess() {
      if (this.isPopup) {
        this.setCurrentPopup();
      } else {
        this.onLoadInitial();
      }
    },
    isStep(name) {
      return this.currentStep === name;
    },
    cleanup() {
      if (typeof PayWithMyBank !== 'undefined') {
        PayWithMyBank.destroy();
      }
    },
    handleAmountBlur() {
      this.$gtm.trackEvent(withMeta({
        event: 'eventTrackerTrigger',
        category: 'Deposit',
        action: 'fill',
        label: 'Amount',
      }));
    },
    handleDepositFail(error) {
      this.currentStepData = {
        ...this.currentStepData,
        error: get(error, 'response.data.message', ''),
      };
      this.currentStep = STEPS.FINISHED_FAILED;
    },
    onRemove(account) {
      this.errorMessage = '';
      this.setCurrentSmallPopup(MODALS.SMALL_PWMB_ACCOUNT_CONFIRM_DELETE);
      this.smallPopupShown = true;
      this.setCurrentPopupOptions({
        onConfirm: () => {
          this.loaderAdd();
          this.deletePWMBAccount(account.authorizationId)
            .then((data) => {
              if (data) {
                this.setup({ loadWidget: false });
              } else {
                this.errorMessage = ERROR_MESSAGE_GENERIC;
              }
              this.loaderRemove();
              this.smallPopupShown = false;
              this.setCurrentSmallPopup();
            })
            .catch(() => {
              this.errorMessage = ERROR_MESSAGE_GENERIC;
              this.loaderRemove();
              this.smallPopupShown = false;
              this.setCurrentSmallPopup();
            });
        },
      });
    },
    onSelect(account) {
      if (account.accountStatus === 2) {
        this.authorizationId = account.authorizationId;
        this.account = account;
        this.setup({ authorizationId: account.authorizationId });
      } else {
        this.authorizationId = account.authorizationId;
        this.account = account;
        this.cleanup();
        this.currentStep = STEPS.AMOUNT_CONFIRM;
      }
    },
    onLoadInitial() {
      this.currentStep = STEPS.INITIAL;
      this.setup();
    },
    onCancelConfirm() {
      this.currentStep = STEPS.AMOUNT_CONFIRM;
    },
    onReviewAndConfirm() {
      this.$gtm.trackEvent(withMeta({
        event: 'eventTrackerTrigger',
        category: 'Deposit',
        action: 'submit',
        label: 'Submit Deposit',
      }));
      if (this.formIsValid() && this.formDisable()) {
        this.loaderAdd();
        const payload = {
          amount: this.amount,
        };
        this.confirmDepositPWMB(payload)
          .then((data) => {
            this.loaderRemove();
            this.formEnable();
            if (this.checkDocumentUploadFlags(data)) return;
            this.currentStep = STEPS.CONFIRM;
          })
          .catch((error) => {
            this.loaderRemove();
            if (this.checkDocumentUploadFlags(error.response.data)) return;
            this.formEnable();
            this.handleDepositFail(error);
          });
      }
    },
    onSubmit() {
      this.formDisable();
      this.$gtm.trackEvent(withMeta({
        event: 'eventTrackerTrigger',
        category: 'Deposit Confirm',
        action: 'submit',
        label: 'Confirm Deposit',
      }));
      this.loaderAdd();
      const payload = {
        amount: this.amount,
        pwmbAuthorizationId: this.authorizationId,
      };
      this.submitDepositPWMB(payload)
        .then((data) => {
          this.loaderRemove();
          if (this.checkDocumentUploadFlags(data)) return;
          this.$gtm.trackEvent(withMeta({
            event: this.status.hasBeenFunded ? 'MultiDeposit' : 'Deposit',
          }));
          if (data.isBankReauthorizationRequired) {
            this.currentStep = STEPS.FINISHED_EXPIRED;
            this.setupReauthorization();
          } else if (data.waitForResponseEvent) {
            this.currentStep = STEPS.PROCESSING;
          } else {
            setTimeout(
              () => { this.setCurrentPopup(); },
              CLOSE_PAYMENT_SUCCESS_TIMEOUT,
            );
          }
          this.formEnable();
        })
        .then(this.getBalance)
        .catch((error) => {
          this.loaderRemove();
          if (this.checkDocumentUploadFlags(error.response.data)) return;
          this.formEnable();
          this.handleDepositFail(error);
        });
    },
    setup({ loadWidget = true, authorizationId = '' } = {}) {
      const url = window.location.origin + window.location.pathname;
      const payload = {
        returnUrl: `${url}?pwmbDeposit=success`,
        cancelUrl: `${url}?pwmbDeposit=cancel`,
        authorizationId,
      };
      this.loaderAdd();
      return this.getDepositPWMB(payload)
        .then((data) => {
          this.loaderRemove();
          this.checkDocumentUploadFlags(data);
          this.errorMessage = data.errorMessage;
          this.promoCodeDetail = data.promoCodeDetail || {};
          this.canDeposit = data.canDeposit;
          this.existingAccounts = data.existingAccounts || [];
          if (loadWidget && data.refreshDataObject && this.$refs.paywithmybank) {
            this.loadWidget(data);
          }
          return data;
        })
        .catch((error) => {
          this.loaderRemove();
          this.errorMessage = get(error, 'response.data.message', null);
        });
    },
    loadWidget(apiData) {
      this.refreshDataObject = apiData.refreshDataObject;
      this.amount = apiData.refreshDataObject.amount;
      this.requestSignature = apiData.pwmbRequestSignature;
      const url = apiData.refreshDataObject.pwmbSDKUrl;
      if (this.$refs.paywithmybank) {
        this.loaderAdd(this.$refs.paywithmybank.parentElement, 'is-loading', false);
      }
      loadScript(url, 'PayWithMyBank')
        .then(() => {
          setTimeout(() => {
            if (this.$refs.paywithmybank) {
              this.loaderRemove(this.$refs.paywithmybank.parentElement, 'is-loading', false);
            }
          }, 2000);
          const options = {
            closeButton: false,
            dragAndDrop: true,
            widgetContainerId: this.payWithMyBankId,
          };
          const data = omitBy({
            ...apiData.refreshDataObject,
            requestSignature: apiData.pwmbRequestSignature,
          }, isNil);

          PayWithMyBank.addPanelListener((command, obj) => {
            switch (command) {
              case 'event':
                switch (obj.type) {
                  case 'close':
                    if (this.currentStep === STEPS.FINISHED_EXPIRED) {
                      this.onLoadInitial();
                    }
                    break;
                  case 'new_location':
                    obj.preventDefault();
                    break;
                  default:
                    break;
                }
                break;
              default:
                break;
            }
          });
          if (apiData.refreshDataObject.authToken) {
            PayWithMyBank.establish(data, options);
          } else {
            PayWithMyBank.selectBankWidget(data, options);
          }
        });
    },
    onUpdateAmount(value) {
      if (this.amount === '' || Number(this.amount) === 0.0) {
        this.amount = value;
      } else if (isNumber(this.amount)) {
        this.amount = Number(this.amount) + value;
      } else {
        this.amount = Number(this.amount) + value;
      }
      this.$gtm.trackEvent(withMeta({
        event: 'eventTrackerTrigger',
        category: 'Deposit',
        action: 'click',
        label: `Amount ${value} Button`,
      }));
    },
    setupReauthorization() {
      this.timeout = window.setTimeout(() => {
        this.setup({
          authorizationId: this.account.authorizationId,
        });
      }, 3500);
    },
    onKeyDown(e) {
      if (e.keyCode === 13 && this.smallPopupShown) {
        this.smallPopupShown = false;
        this.setCurrentSmallPopup();
      }
    },
    currentStepHandler(value) {
      this.$emit('step', value);
    },
  },
  watch: {
    bankTransferStep(value) {
      this.currentStep = value.step;
      this.currentStepData = value.stepData;
      if (value.step === STEPS.AMOUNT_CONFIRM) {
        this.cleanup();
        this.setup({ loadWidget: false })
          .then((data) => {
            this.authorizationId = this.currentStepData.pwmbAuthorizationId;
            this.account = data.existingAccounts
              .find(i => i.authorizationId === this.authorizationId);
          });
      } else if (value.step === STEPS.FINISHED_EXPIRED) {
        this.cleanup();
        this.setup({ loadWidget: false })
          .then((data) => {
            this.authorizationId = this.currentStepData.pwmbAuthorizationId;
            this.account = data.existingAccounts
              .find(i => i.authorizationId === this.authorizationId);
          })
          .then(() => this.setupReauthorization());
      } else if (value.step === STEPS.CONFIRM) {
        this.amount = Number(this.currentStepData.prefilledAmountToDeposit);
      }
    },
    currentStep: {
      handler: 'currentStepHandler',
      immediate: true,
    },
  },
  beforeDestroy() {
    this.cleanup();
    window.removeEventListener('keyup', this.onKeyDown);
    if (this.timeout) {
      window.clearTimeout(this.timeout);
    }
  },
  mounted() {
    this.setup();
    this.getDepositMethods();
    window.addEventListener('keyup', this.onKeyDown);
  },
};



// WEBPACK FOOTER //
// ./src/components/wallet/add-funds-bank-transfer/add-funds-bank-transfer.js